import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Modal from "@mui/material/Modal";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "../hooks/useTranslation";
import { notificationsAPI } from "../backend/api/api";

const STORAGE_KEY_PREFIX = "importInProgress_";
const TTL_MS = 15 * 60 * 1000; // 15 minutes

// How long the blocking overlay stays fully blocking (no way out) before the
// "Continue in the background" link appears on the card. Measured from the
// persisted `startedAt`, never from a component's own mount time, so a page
// reload mid-import picks up wherever real elapsed time landed instead of
// restarting the clock (see the background-link timer effect below).
const BACKGROUND_LINK_DELAY_MS = 60 * 1000; // 60 seconds

// The overlay must out-rank EVERY other stacked layer in the app — picking a
// number "by eye" (the original 10000 was) is exactly how this regresses,
// because several existing components already sit at or above that:
//   - RegularCategoriesFilter / ScheduledCategoriesFilter popovers: 10001
//     (src/components/ContactsPage/RegularCategoriesFilter/RegularCategoriesFilter.tsx,
//     src/components/ContactsPage/ScheduledCategoriesFilter/index.tsx) —
//     render on the SAME Contacts page imports start from.
//   - ManualRegionDialog / TextOnlyAddressConfirmDialog: 10002
//     (src/components/base/ManualRegionDialog, .../TextOnlyAddressConfirmDialog)
//   - ManualRegionPicker's address-autocomplete popper: 10010
//     (src/components/base/ManualRegionPicker)
//   - AlertNotifications panel: 99999 (src/components/AlertNotifications/styled.scss)
//   - `.pac-container` (Google Places autocomplete suggestion list, forced
//     with !important so it's reachable from inside any dialog): 100000
//     (src/index.scss) — the true maximum found across the whole src/ tree
//     (scss/css included) as of this fix.
// IMPORT_OVERLAY_Z_INDEX must stay comfortably above ALL of these with a
// clear margin, not just the current max — src/contexts/__tests__ has a
// test that scans every z-index/zIndex declaration in src/ and fails if any
// of them reaches this value, so a future component that goes higher is
// caught immediately instead of silently rendering over a "blocking" overlay.
export const IMPORT_OVERLAY_Z_INDEX = 200000;

// Polling fallback (below): Pusher does not replay missed events, so a
// dropped websocket, a closed tab, or a reload mid-import used to leave the
// banner (and the un-refreshed contacts list) stuck until a manual browser
// refresh. Polling re-checks the same two notification types Pusher already
// listens for, so either path can detect completion.
const POLL_INTERVAL_MS = 10 * 1000; // 10 seconds
const IMPORT_COMPLETED_TYPE = "ImportCompletedNotification";
const IMPORT_ABORTED_TYPE = "ImportAbortedNotification";
// How many recent notifications to request per poll/baseline check. We still
// pass typeSearch so the payload stays small once the backend type filter is
// fixed, but we can't rely on the server having actually filtered (see
// findNewestImportNotificationId below) — 10 gives the client-side match
// enough rows to find an import notification even if the server ignored the
// filter and returned a mix of types.
const NOTIFICATIONS_PAGE_SIZE = 10;

/**
 * Finds the newest ImportCompleted/AbortedNotification in `rows`, id AND type.
 *
 * The server is asked to filter to just those two types (`typeSearch`), but
 * cannot be trusted to have actually done so — `app/Filters/Notification/Type.php`
 * compares against a doubled-backslash class name and currently matches
 * nothing, so `rows` may come back as an unfiltered, mixed-type page. This
 * walks the rows client-side and returns the first (i.e. newest, per the
 * endpoint's `notifications()->latest()` / created_at-desc ordering) row
 * whose own `type` field matches, ignoring anything else in between.
 * Returns null when no row of either import type is present.
 */
function findNewestImportNotification(
  rows: unknown
): { id: string; type: string } | null {
  if (!Array.isArray(rows)) return null;
  const match = rows.find(
    (row: any) =>
      row?.type === IMPORT_COMPLETED_TYPE || row?.type === IMPORT_ABORTED_TYPE
  );
  return match ? { id: match.id, type: match.type } : null;
}

// Thin wrapper kept for the one call site (the baseline capture in
// startImport()) that only ever needed the id, never the type.
function findNewestImportNotificationId(rows: unknown): string | null {
  return findNewestImportNotification(rows)?.id ?? null;
}

interface StoredImportState {
  startedAt: number;
  // The id of the newest ImportCompleted/AbortedNotification that already
  // existed when this import started (or null when there were none yet) —
  // the baseline the poll below compares against to notice a genuinely NEW
  // one. `undefined` means the baseline lookup hasn't resolved (or failed):
  // comparisons are skipped until it lands, and if it never does, polling
  // just stays a no-op for this import (Pusher + the TTL remain the fallback).
  baselineNotificationId?: string | null;
  // True once the user has clicked "Continue in the background" on the
  // blocking overlay. Persisted so a reload after choosing it restores the
  // small banner, not the overlay — detection (Pusher/poll/TTL) keeps
  // running exactly the same either way.
  backgroundMode?: boolean;
}

// What actually happened to the import the overlay was tracking. Passed to
// the registered onCompleted callback (see setOnCompleted) so a caller that
// cares — the registration ContactIntakePanels does, the Contacts page
// doesn't and can safely ignore the argument — can show the right message
// instead of a generic "done".
export type ImportOutcome = "completed" | "aborted";

export interface ImportProgressContextValue {
  isImportInProgress: boolean;
  startImport: () => void;
  clearImport: () => void;
  setOnCompleted: (cb: ((outcome: ImportOutcome) => void) | null) => void;
}

const ImportProgressContext = createContext<ImportProgressContextValue | null>(
  null
);

function getStorageKey(userId: number): string {
  return `${STORAGE_KEY_PREFIX}${userId}`;
}

function readFromStorage(userId: number): StoredImportState | null {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (!raw) return null;
    const parsed: StoredImportState = JSON.parse(raw);
    if (typeof parsed?.startedAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeToStorage(userId: number, state: StoredImportState): void {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(state));
  } catch {
    // Private browsing or storage quota exceeded — safe to ignore
  }
}

function clearFromStorage(userId: number): void {
  try {
    localStorage.removeItem(getStorageKey(userId));
  } catch {
    // Ignore
  }
}

function isStoredStateValid(stored: StoredImportState): boolean {
  return Date.now() - stored.startedAt < TTL_MS;
}

export interface ImportProgressProviderProps {
  userId: number | undefined;
  children: React.ReactNode;
}

export function ImportProgressProvider({
  userId,
  children,
}: ImportProgressProviderProps): JSX.Element {
  const { t } = useTranslation();

  const [isImportInProgress, setIsImportInProgress] = useState<boolean>(() => {
    if (!userId) return false;
    const stored = readFromStorage(userId);
    if (!stored) return false;
    return isStoredStateValid(stored);
  });

  // Whether the user has chosen "Continue in the background" for the
  // CURRENT import — while true, the small Snackbar banner shows instead of
  // the blocking overlay. Initialized from storage so a reload after
  // choosing it does not bring the overlay back.
  const [isBackgroundMode, setIsBackgroundMode] = useState<boolean>(() => {
    if (!userId) return false;
    const stored = readFromStorage(userId);
    return !!stored?.backgroundMode;
  });

  // Whether the "Continue in the background" link has appeared yet on the
  // overlay card (BACKGROUND_LINK_DELAY_MS after the persisted startedAt).
  // Initialized from storage too, so a reload well past the mark shows the
  // link immediately instead of waiting out a fresh 60s.
  const [canContinueInBackground, setCanContinueInBackground] = useState<boolean>(
    () => {
      if (!userId) return false;
      const stored = readFromStorage(userId);
      if (!stored) return false;
      return Date.now() - stored.startedAt >= BACKGROUND_LINK_DELAY_MS;
    }
  );

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const backgroundLinkTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const onCompletedRef = useRef<((outcome: ImportOutcome) => void) | null>(
    null
  );

  // Polling fallback bookkeeping (below). `completionHandledRef` is the
  // shared guard between the Pusher listener and the poll: whichever path
  // notices completion first wins, and the other becomes a no-op — never a
  // double refresh. `pollIntervalRef`/`pollInFlightRef` track the recurring
  // check itself.
  const completionHandledRef = useRef(false);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollInFlightRef = useRef(false);
  // Set by a fresh startImport() call and consumed by the very next run of
  // the polling effect below, so that run skips its immediate check. A
  // fresh start already kicks off its own baseline-establishing request —
  // an immediate poll check right as the banner appears would just be a
  // second, redundant request racing it. This can't be inferred from
  // "is baselineNotificationId defined yet" at effect-run time: React can
  // batch the isImportInProgress update so the effect's first run lands
  // AFTER the (fast) baseline request has already resolved and written a
  // baseline, making that look identical to a reload picking up an
  // already-established one.
  const skipNextImmediateCheckRef = useRef(false);

  // -------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current !== null) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  }, []);

  const clearImport = useCallback(() => {
    if (!userId) return;
    clearFromStorage(userId);
    setIsImportInProgress(false);
    setIsBackgroundMode(false);
    setCanContinueInBackground(false);
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (backgroundLinkTimeoutRef.current !== null) {
      clearTimeout(backgroundLinkTimeoutRef.current);
      backgroundLinkTimeoutRef.current = null;
    }
  }, [userId]);

  const runOnCompleted = useCallback(
    (outcome: ImportOutcome) => {
      if (onCompletedRef.current) {
        onCompletedRef.current(outcome);
      }
      clearImport();
    },
    [clearImport]
  );

  // Shared completion handler for BOTH the Pusher event and the poll: only
  // the first caller runs runOnCompleted (which fires the app's callback and
  // clears the banner) — the loser is a no-op.
  const handleImportFinished = useCallback(
    (outcome: ImportOutcome) => {
      if (completionHandledRef.current) return;
      completionHandledRef.current = true;
      stopPolling();
      runOnCompleted(outcome);
    },
    [runOnCompleted, stopPolling]
  );

  const startImport = useCallback(() => {
    if (!userId) return;
    // Guard: prevent double-firing if already in progress
    if (isImportInProgress) return;

    completionHandledRef.current = false;
    skipNextImmediateCheckRef.current = true;

    const stored: StoredImportState = { startedAt: Date.now() };
    writeToStorage(userId, stored);
    setIsImportInProgress(true);
    setIsBackgroundMode(false);
    setCanContinueInBackground(false);

    // Backup auto-clear after TTL
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      clearImport();
    }, TTL_MS);

    // Record the polling baseline (the newest import-completion/abort
    // notification that already exists, if any) so the poll below can tell
    // a genuinely NEW one apart from one that predates this import. This is
    // fire-and-forget: if it fails (e.g. a 403 for a limited-permission
    // user), baselineNotificationId is simply never set, the poll stays a
    // no-op for this import, and Pusher + the TTL remain the fallback —
    // no error toast, no console noise, no crash.
    //
    // Known edge case, deliberately left this way: if the page reloads in
    // the sub-second window between the writeToStorage() above and this
    // request resolving, the request itself dies with the page — there's no
    // way to resume or retry it after reload, since nothing else re-fires
    // it. baselineNotificationId then stays undefined for the rest of this
    // import (readFromStorage/checkForCompletion's own
    // `baselineNotificationId === undefined` guard keeps every later poll
    // tick a no-op, forever, same as the outright-failure path above).
    // Pusher and the 15-minute TTL still cover completion in that case —
    // this is exactly today's pre-polling behaviour, just for a narrower
    // slice of reloads than before.
    const capturedUserId = userId;
    notificationsAPI
      .getNotifications({
        perPage: NOTIFICATIONS_PAGE_SIZE,
        page: 1,
        typeSearch: [IMPORT_COMPLETED_TYPE, IMPORT_ABORTED_TYPE],
      })
      .then((response: any) => {
        const rows = response?.data?.data;
        const newestId = findNewestImportNotificationId(rows);
        const current = readFromStorage(capturedUserId);
        if (!current) return; // already cleared (fast Pusher completion, or manual close)
        writeToStorage(capturedUserId, { ...current, baselineNotificationId: newestId });
      })
      .catch(() => {
        // Swallow — see comment above.
      });
  }, [userId, isImportInProgress, clearImport]);

  // Re-runs the same small, type-filtered notifications request every
  // POLL_INTERVAL_MS while the banner is showing (item 2 of the fallback),
  // comparing the newest id against the stored baseline.
  const checkForCompletion = useCallback(async () => {
    if (!userId) return;
    if (completionHandledRef.current) return;
    if (pollInFlightRef.current) return;
    pollInFlightRef.current = true;
    try {
      const response: any = await notificationsAPI.getNotifications({
        perPage: NOTIFICATIONS_PAGE_SIZE,
        page: 1,
        typeSearch: [IMPORT_COMPLETED_TYPE, IMPORT_ABORTED_TYPE],
      });
      const rows = response?.data?.data;
      const newest = findNewestImportNotification(rows);
      const newestId = newest?.id ?? null;

      const stored = readFromStorage(userId);
      if (!stored) return; // cleared while the request was in flight
      if (stored.baselineNotificationId === undefined) return; // baseline not established yet

      if (newestId !== stored.baselineNotificationId) {
        handleImportFinished(
          newest?.type === IMPORT_ABORTED_TYPE ? "aborted" : "completed"
        );
      }
    } catch {
      // Swallow — e.g. a 403 for a limited-permission user — and stop
      // retrying so we don't keep hammering a request this user can't make.
      // No error toast, no console error, no crash: today's Pusher/TTL
      // behaviour is the fallback.
      stopPolling();
    } finally {
      pollInFlightRef.current = false;
    }
  }, [userId, handleImportFinished, stopPolling]);

  const setOnCompleted = useCallback(
    (cb: ((outcome: ImportOutcome) => void) | null) => {
      onCompletedRef.current = cb;
    },
    []
  );

  // Escape hatch (item 1): switches the CURRENT import from the blocking
  // overlay to the small background banner. Detection (Pusher/poll/TTL) and
  // completion handling are untouched — only which UI is shown changes.
  const continueInBackground = useCallback(() => {
    if (!userId) return;
    const stored = readFromStorage(userId);
    if (stored) {
      writeToStorage(userId, { ...stored, backgroundMode: true });
    }
    setIsBackgroundMode(true);
  }, [userId]);

  // -------------------------------------------------------------------
  // Background-link timer: the overlay's "Continue in the background" link
  // is hidden for the first BACKGROUND_LINK_DELAY_MS and then appears.
  // Measured from the persisted `startedAt` (not this effect's own mount
  // time), so a reload mid-import picks up wherever real elapsed time
  // landed — showing the link immediately if that's already past the mark,
  // or after a shorter remaining wait otherwise. Runs on every transition
  // into "in progress" (a fresh startImport(), or storage already showing
  // one in progress on mount).
  // -------------------------------------------------------------------
  useEffect(() => {
    if (backgroundLinkTimeoutRef.current !== null) {
      clearTimeout(backgroundLinkTimeoutRef.current);
      backgroundLinkTimeoutRef.current = null;
    }

    if (!userId || !isImportInProgress) {
      return;
    }

    const stored = readFromStorage(userId);
    if (!stored) return;

    const remaining = BACKGROUND_LINK_DELAY_MS - (Date.now() - stored.startedAt);
    if (remaining <= 0) {
      setCanContinueInBackground(true);
      return;
    }

    setCanContinueInBackground(false);
    backgroundLinkTimeoutRef.current = setTimeout(() => {
      setCanContinueInBackground(true);
    }, remaining);

    return () => {
      if (backgroundLinkTimeoutRef.current !== null) {
        clearTimeout(backgroundLinkTimeoutRef.current);
        backgroundLinkTimeoutRef.current = null;
      }
    };
  }, [userId, isImportInProgress]);

  // -------------------------------------------------------------------
  // On mount AND whenever `userId` changes: restore ALL persisted state
  // (not just re-arm the TTL timeout) from storage, or reset it if there is
  // none/it's expired.
  //
  // This is the fix for a real page-reload bug: the provider is mounted as
  // `<ImportProgressProvider userId={currentUser?.id}>` (App/index.tsx), and
  // on an actual reload `currentUser` resolves ASYNCHRONOUSLY — so the very
  // first render has `userId === undefined`, every `useState(() => …)`
  // initializer above short-circuits to `false`, and (before this fix) this
  // effect only ever re-armed the TTL timeout for an already-true
  // `isImportInProgress` — it never SET `isImportInProgress` true in the
  // first place once `userId` arrived, so a reload mid-import silently lost
  // both the overlay/banner AND polling (itself gated on
  // `isImportInProgress`), meaning completion was never even detected.
  //
  // Runs this same restore logic on every `userId` change, not just the
  // very first time it becomes truthy, so switching to a different
  // signed-in user (or logging out, `userId` going back to undefined) also
  // can't leave a previous user's overlay/banner state stuck on screen —
  // each branch below explicitly resets all three states before deciding
  // whether there's something valid to restore for the CURRENT `userId`.
  // -------------------------------------------------------------------
  useEffect(() => {
    // Clear any timer armed by a previous run (a different userId, or a
    // previous validation of the same one) before this run decides what it
    // needs — never leaves a stale timeout pointed at the wrong user/state.
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (!userId) {
      // No signed-in user yet (still loading) or just logged out — nothing
      // to restore, and nothing from a previous user should linger.
      setIsImportInProgress(false);
      setIsBackgroundMode(false);
      setCanContinueInBackground(false);
      return;
    }

    const stored = readFromStorage(userId);
    if (!stored || !isStoredStateValid(stored)) {
      // Expired, absent, or belongs to a user with no import running —
      // ensure state is clear rather than carrying over anything stale.
      setIsImportInProgress(false);
      setIsBackgroundMode(false);
      setCanContinueInBackground(false);
      clearFromStorage(userId);
      return;
    }

    // Still valid — restore the full UI state (this is the actual fix) and
    // arm a backup TTL timeout for the remaining duration. Once
    // `isImportInProgress` becomes true here, the background-link timer
    // effect and the polling effect (both keyed on it) pick up and arm
    // themselves automatically — no separate wiring needed for either.
    setIsImportInProgress(true);
    setIsBackgroundMode(!!stored.backgroundMode);
    setCanContinueInBackground(
      Date.now() - stored.startedAt >= BACKGROUND_LINK_DELAY_MS
    );

    const remaining = TTL_MS - (Date.now() - stored.startedAt);
    timeoutRef.current = setTimeout(() => {
      clearImport();
    }, remaining);

    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // -------------------------------------------------------------------
  // visibilitychange: re-check TTL when tab becomes active
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!userId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!isImportInProgress) return;

      const stored = readFromStorage(userId);
      if (!stored || !isStoredStateValid(stored)) {
        clearImport();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userId, isImportInProgress, clearImport]);

  // -------------------------------------------------------------------
  // Cross-tab sync via storage event
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!userId) return;

    const storageKey = getStorageKey(userId);

    const handleStorageEvent = (event: StorageEvent) => {
      if (event.key !== storageKey) return;

      if (event.newValue === null) {
        // Another tab cleared the key
        setIsImportInProgress(false);
        setIsBackgroundMode(false);
        setCanContinueInBackground(false);
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        if (backgroundLinkTimeoutRef.current !== null) {
          clearTimeout(backgroundLinkTimeoutRef.current);
          backgroundLinkTimeoutRef.current = null;
        }
      } else {
        // Another tab set the key — parse and validate
        try {
          const stored: StoredImportState = JSON.parse(event.newValue);
          if (typeof stored?.startedAt === "number" && isStoredStateValid(stored)) {
            setIsImportInProgress(true);
            // Mirror the "Continue in the background" choice across tabs too:
            // if another tab picked it, this tab must swap its own overlay
            // for the banner rather than leaving it stuck up (this was the
            // fix's own gap — the flag was persisted but never re-read here).
            setIsBackgroundMode(!!stored.backgroundMode);
          }
        } catch {
          // Malformed value — ignore
        }
      }
    };

    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [userId]);

  // -------------------------------------------------------------------
  // Polling fallback: while the banner is showing, re-check for completion
  // every POLL_INTERVAL_MS even if the Pusher event never arrives (dropped
  // websocket, closed tab, or a reload mid-import). Also runs one immediate
  // check on mount when the flag is already set from storage, covering a
  // page reload mid-import. Stops on completion, when the banner clears
  // (TTL or manual close), and on unmount — never leaves an interval running.
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!userId || !isImportInProgress) {
      stopPolling();
      return;
    }

    // Skip the immediate check right after a fresh startImport() — it just
    // kicked off its own baseline-establishing request, so an immediate
    // poll here would only be a second, redundant request racing it (see
    // skipNextImmediateCheckRef's own comment). Otherwise this is a reload
    // picking up an import started in a previous session, so check right
    // away instead of waiting out the first 10-second tick.
    if (skipNextImmediateCheckRef.current) {
      skipNextImmediateCheckRef.current = false;
    } else {
      void checkForCompletion();
    }

    pollIntervalRef.current = setInterval(() => {
      void checkForCompletion();
    }, POLL_INTERVAL_MS);

    return () => {
      stopPolling();
    };
  }, [userId, isImportInProgress, checkForCompletion, stopPolling]);

  // -------------------------------------------------------------------
  // Pusher: listen for ImportCompletedNotification and ImportAbortedNotification
  // -------------------------------------------------------------------
  useEffect(() => {
    if (!userId) return;
    if (!(window as any).Echo) return;

    const echo = (window as any).Echo;
    const channel = echo.private(`admin.${userId}`);

    const handler = (notification: any) => {
      const type: string | undefined = notification?.type;
      if (
        type !== IMPORT_COMPLETED_TYPE &&
        type !== IMPORT_ABORTED_TYPE
      ) {
        return;
      }
      handleImportFinished(type === IMPORT_ABORTED_TYPE ? "aborted" : "completed");
    };

    channel.listen("BroadcastNotificationToAdminEvent", handler);

    return () => {
      try {
        channel.stopListening("BroadcastNotificationToAdminEvent", handler);
      } catch {
        // Ignore cleanup errors
      }
    };
  }, [userId]);

  // -------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------
  return (
    <ImportProgressContext.Provider
      value={{ isImportInProgress, startImport, clearImport, setOnCompleted }}
    >
      {children}
      {/* Blocking processing overlay: shown app-wide for the whole app tree
          (this provider is mounted once, high in App/index.tsx) whenever an
          import is in progress and the user hasn't chosen to continue in the
          background. Plain MUI Modal (not Dialog) so there is no Fade
          transition to race in tests, and so we control every closability
          knob explicitly: no `onClose` prop means a backdrop click is a
          no-op, `disableEscapeKeyDown` means Escape is a no-op too, and
          Modal's own focus trap (on by default) keeps Tab from reaching the
          page behind it. */}
      <Modal
        open={isImportInProgress && !isBackgroundMode}
        disableEscapeKeyDown
        sx={{
          zIndex: IMPORT_OVERLAY_Z_INDEX,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Box
          role="alertdialog"
          aria-modal="true"
          aria-busy="true"
          aria-labelledby="import-progress-overlay-title"
          aria-describedby="import-progress-overlay-body"
          tabIndex={-1}
          sx={{
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxWidth: 360,
            outline: "none",
          }}
        >
          <CircularProgress size={40} sx={{ mb: 2 }} aria-labelledby="import-progress-overlay-title" />
          <Typography
            id="import-progress-overlay-title"
            variant="h6"
            component="h2"
            sx={{ mb: 1 }}
          >
            {t("importProgress.overlay_title")}
          </Typography>
          <Typography
            id="import-progress-overlay-body"
            variant="body2"
            color="text.secondary"
          >
            {t("importProgress.overlay_body")}
          </Typography>
          {canContinueInBackground && (
            <Box
              component="button"
              type="button"
              onClick={continueInBackground}
              sx={{
                mt: 3,
                border: "none",
                background: "none",
                color: "primary.main",
                cursor: "pointer",
                fontSize: 13,
                textDecoration: "underline",
                p: 0,
              }}
            >
              {t("importProgress.continue_in_background")}
            </Box>
          )}
        </Box>
      </Modal>
      <Snackbar
        open={isImportInProgress && isBackgroundMode}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: "64px" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          action={
            <IconButton
              size="small"
              onClick={clearImport}
              aria-label={t("common.close")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {t("importProgress.banner")}
        </Alert>
      </Snackbar>
    </ImportProgressContext.Provider>
  );
}

export function useImportProgress(): ImportProgressContextValue {
  const ctx = useContext(ImportProgressContext);
  if (!ctx) {
    throw new Error(
      "useImportProgress must be used within ImportProgressProvider"
    );
  }
  return ctx;
}
